import { useState } from 'react';

/**
 * Hook para calcular o consumo de UVBLOCK
 * 
 * Rendimentos:
 * - Balde 3,6kg (3,6L): rende até 45m²
 * - Balde 18kg (18L): rende até 225m²
 * - Consumo médio: 80g/m² por demão
 * 
 * Lógica de cálculo:
 * - Para áreas até 45m²: usar apenas baldes de 3,6kg (evita desperdício)
 * - Para áreas de 46m² a 225m²: usar 1 balde de 18kg
 * - Para áreas maiores: usar combinação otimizada
 */
export const useConsumptionCalculator = () => {
  const [area, setArea] = useState('');
  const [consumption, setConsumption] = useState(null);

  // Constantes de rendimento
  const BUCKET_36_COVERAGE = 45;   // m² por balde de 3,6kg
  const BUCKET_18_COVERAGE = 225;  // m² por balde de 18kg
  const CONSUMPTION_PER_M2 = 0.08; // 80g/m² = 0.08kg/m²

  const calculateConsumption = () => {
    if (!area || parseFloat(area) <= 0) return;

    const areaNum = parseFloat(area);
    const totalKg = (areaNum * CONSUMPTION_PER_M2).toFixed(2);

    let buckets18 = 0;
    let buckets36 = 0;

    // Lógica principal:
    // 1. Se a área for até 45m², usa apenas balde de 3,6kg (sem desperdício)
    // 2. Se a área for maior que 45m² e até 225m², usa 1 balde de 18kg
    // 3. Se a área for maior que 225m², calcula a combinação otimizada

    if (areaNum <= BUCKET_36_COVERAGE) {
      // Área pequena: usar apenas balde(s) de 3,6kg
      buckets36 = Math.ceil(areaNum / BUCKET_36_COVERAGE);
      buckets18 = 0;
    } else if (areaNum <= BUCKET_18_COVERAGE) {
      // Área média: usar 1 balde de 18kg (mais econômico que múltiplos de 3,6kg)
      buckets18 = 1;
      buckets36 = 0;
    } else {
      // Área grande: calcular combinação otimizada
      // Primeiro, quantos baldes de 18kg são necessários
      buckets18 = Math.floor(areaNum / BUCKET_18_COVERAGE);
      const remainingArea = areaNum - (buckets18 * BUCKET_18_COVERAGE);

      if (remainingArea > 0) {
        // Para a área restante, decidir se usa mais um de 18kg ou de 3,6kg
        // Se restante > 45m², vale mais adicionar outro de 18kg
        // Se restante <= 45m², usa balde(s) de 3,6kg
        if (remainingArea <= BUCKET_36_COVERAGE) {
          buckets36 = Math.ceil(remainingArea / BUCKET_36_COVERAGE);
        } else {
          // Se restante > 45m² mas < 225m², verificar qual opção gera menos desperdício
          const wastageWith18 = BUCKET_18_COVERAGE - remainingArea;
          const neededBuckets36 = Math.ceil(remainingArea / BUCKET_36_COVERAGE);
          const wastageWith36 = (neededBuckets36 * BUCKET_36_COVERAGE) - remainingArea;

          // Escolher a opção com menos desperdício
          if (wastageWith18 <= wastageWith36) {
            buckets18 += 1;
          } else {
            buckets36 = neededBuckets36;
          }
        }
      }
    }

    setConsumption({
      totalKg,
      buckets36,
      buckets18,
      area: areaNum
    });
  };

  const resetCalculator = () => {
    setArea('');
    setConsumption(null);
  };

  return {
    area,
    setArea,
    consumption,
    calculateConsumption,
    resetCalculator,
    BUCKET_36_COVERAGE,
    BUCKET_18_COVERAGE
  };
};

export default useConsumptionCalculator;
